import {assertAdvanceRoomWindow} from './bookingAdvance.js';
import {doc, getDocFromServer, runTransaction} from 'firebase/firestore';
const JANAKPURI_HOTEL_ID='Hotel Dev Palace - Jankpuri';

// The Firestore transaction runner assumes a FirebaseError has a Firestore code.
// Functions errors (functions/internal, functions/aborted, ...) use another code
// namespace. Preserve their code for our retries, but give the runner a plain
// application Error so it cannot poison the Firestore AsyncQueue with an assert.
function pmsCallbackError(error) {
  if (error?.name === 'FirebaseError' && String(error.code || '').startsWith('functions/')) {
    const wrapped = new Error(error.message || 'Janakpuri booking service unavailable.');
    wrapped.name = 'PmsBookingError';
    wrapped.code = error.code;
    wrapped.cause = error;
    return wrapped;
  }
  return error;
}

async function janakpuriProtectionEnabled(database) {
  try {
    const snapshot = await getDocFromServer(doc(database, 'janakpuriInventory', 'calendar'));
    return snapshot.exists() && snapshot.data()?.enabled === true;
  } catch (error) {
    // Before the add-on rules are deployed, this new collection is denied.
    // Keep normal PMS setup available. Enabled production rules still require
    // backend permits, so this fallback cannot authorize an unprotected write.
    if (error.code === 'permission-denied') return false;
    throw error;
  }
}


// Revision rules may reject an optimistic transaction before Firestore reports
// its write conflict. Retry only when an authorized fresh read proves that a
// board read by this transaction advanced. Never retry an actual access denial.
export async function runPmsTransaction(database, updateFunction, options) {
  const operationId = `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
  let jpTouched=false;
  for (let attempt = 0; ; attempt += 1) {
    const boards = new Map();
    let jpPermitIssued=false;
    try {
      const outcome = await runTransaction(database, async transaction => {
        try {
        const writes = [];
        const reads = new Map();
        boards.clear();
        const tracked = new Proxy(transaction, {
          get(target, key) {
            if (key === 'get') return async reference => {
              const snapshot = await target.get(reference);
              reads.set(reference.path, snapshot);
              if (reference.path?.startsWith('hotelRoomBoards/')) {
                boards.set(reference.path, {reference, revision: Number(snapshot.data()?.revision || 0)});
              }
              return snapshot;
            };
            if (['set','update','delete'].includes(key)) return (...args) => { writes.push({method:key,args}); return tracked; };
            const value = Reflect.get(target, key, target);
            return typeof value === 'function' ? value.bind(target) : value;
          },
        });
        const result = await updateFunction(tracked);
        // Read the reservation calendar inside this same Firestore transaction.
        // A simultaneous advance reservation reads the board/stays, so either side retries.
        const advanceHotels = new Set();
        for(const w of writes) {
          if(w.args[0].path.startsWith('hotelRoomBoards/')) advanceHotels.add(w.args[0].id);
          if(w.method!=='delete' && /^hotelStays\/[^/]+$/.test(w.args[0].path)) {
            const h=w.args[1].hotelId||reads.get(w.args[0].path)?.data()?.hotelId;
            if(h)advanceHotels.add(h);
          }
        }
        for(const hotel of advanceHotels) {
          if(hotel===JANAKPURI_HOTEL_ID)continue;
          const calendar=await transaction.get(doc(database,'roomAdvanceCalendars',hotel));
          if(!calendar.exists())continue;
          const reservations=calendar.data().reservations||[];
          for(const w of writes) {
            if(w.method==='delete'||!/^hotelStays\/[^/]+$/.test(w.args[0].path))continue;
            let old=reads.get(w.args[0].path);
            if(!old){old=await transaction.get(w.args[0]);reads.set(w.args[0].path,old);}
            const next={...(old.data()||{}),...w.args[1]};
            if(next.hotelId!==hotel)continue;
            const changed=!old.exists()||['room','checkInAt','expectedCheckOutAt','status'].some(k=>next[k]!==old.data()[k]);
            if(changed)assertAdvanceRoomWindow(next,reservations);
          }
          const bw=writes.find(w=>w.args[0].path===`hotelRoomBoards/${hotel}`&&w.method!=='delete');
          if(bw) {
            const before=reads.get(bw.args[0].path)?.data()||{};
            for(const card of bw.args[1].occupiedRooms||[]) {
              const room=typeof card==='string'?card:card.room;
              if((before.occupiedRooms||[]).some(c=>(typeof c==='string'?c:c.room)===room))continue;
              const sw=writes.find(w=>w.method!=='delete'&&w.args[0].path.startsWith('hotelStays/')&&w.args[1].room===room);
              if(!sw)assertAdvanceRoomWindow({room,status:'checked_in'},reservations);
            }
          }
        }
        const touchesJanakpuri = writes.some(w => w.args[0].path === `hotelRoomBoards/${JANAKPURI_HOTEL_ID}`
          || (w.method !== 'delete' && /^hotelStays\/[^/]+$/.test(w.args[0].path)
            && (w.args[1].hotelId || reads.get(w.args[0].path)?.data()?.hotelId) === JANAKPURI_HOTEL_ID));
        const protectJanakpuri = touchesJanakpuri && await janakpuriProtectionEnabled(database);
        // CNU updates the stay ledger without originally touching the board.
        // Include a board revision so it shares the same reservation mutex.
        if (protectJanakpuri && !writes.some(w=>w.args[0].path===`hotelRoomBoards/${JANAKPURI_HOTEL_ID}`)) {
          const timedStay = writes.find(w=>w.method!=='delete' && /^hotelStays\/[^/]+$/.test(w.args[0].path)
            && ['checkInAt','expectedCheckOutAt','room','status'].some(k=>Object.prototype.hasOwnProperty.call(w.args[1],k))
            && (w.args[1].hotelId || reads.get(w.args[0].path)?.data()?.hotelId)===JANAKPURI_HOTEL_ID);
          if(timedStay){
            const ref=doc(database,'hotelRoomBoards',JANAKPURI_HOTEL_ID),snap=await transaction.get(ref);
            reads.set(ref.path,snap);
            boards.set(ref.path,{reference:ref,revision:Number(snap.data()?.revision||0)});
            const current=snap.data()||{};
            writes.push({method:'set',args:[ref,{...current,revision:Number(current.revision||0)+1,schemaVersion:2,hotelId:JANAKPURI_HOTEL_ID},{merge:true}]});
          }
        }
        const boardWrite = writes.find(w => w.args[0].path === `hotelRoomBoards/${JANAKPURI_HOTEL_ID}`);
        if (protectJanakpuri && boardWrite && boardWrite.method !== 'delete') {
          jpTouched=true;
          const {janakpuriCall,stayProjection,getJanakpuriClaim}=await import('../services/janakpuriBookingService.js');
          const base = reads.get(boardWrite.args[0].path)?.data() || {};
          const nextBoard = {...base, ...boardWrite.args[1]};
          const stays = {}, locks = {};
          for (const w of writes) {
            if (w.method === 'delete') continue;
            const ref=w.args[0];
            if (!/^(hotelStays|activeRoomStays)\/[^/]+$/.test(ref.path)) continue;
            let snap=reads.get(ref.path);
            if (!snap) {snap=await transaction.get(ref); reads.set(ref.path,snap);}
            const next={...(snap.data()||{}),...w.args[1]};
            if(next.hotelId!==JANAKPURI_HOTEL_ID)continue;
            if(ref.path.startsWith('hotelStays/')) stays[ref.id]=stayProjection(next);
            else locks[ref.id]={hotelId:next.hotelId,room:next.room||'',activeStayId:next.activeStayId||'',status:next.status||'',quickOccupancyId:next.quickOccupancyId||''};
          }
          const permit=await janakpuriCall('prepare',{operationId, revision:nextBoard.revision,
            board:Object.fromEntries(['vacantRooms','occupiedRooms','checkoutRooms','serviceRooms'].map(k=>[k,nextBoard[k]||[]])),
            stays,locks,claimId:getJanakpuriClaim()});
          if(permit.enabled){
            jpPermitIssued=true;
            boardWrite.args[1]={...boardWrite.args[1],jpPermitId:permit.permitId};
            if(boardWrite.args[2]?.mergeFields)boardWrite.args[2]={...boardWrite.args[2],mergeFields:[...new Set([...boardWrite.args[2].mergeFields,'jpPermitId'])]};
            for(const w of writes){if(w.method==='delete')continue;
              if(stays[w.args[0].id] && w.args[0].path.startsWith('hotelStays/'))w.args[1]={...w.args[1],jpPermitId:permit.permitId};
              if(locks[w.args[0].id] && w.args[0].path.startsWith('activeRoomStays/'))w.args[1]={...w.args[1],jpPermitId:permit.permitId};
              if(w.args[1].jpPermitId===permit.permitId && w.args[2]?.mergeFields)w.args[2]={...w.args[2],mergeFields:[...new Set([...w.args[2].mergeFields,'jpPermitId'])]};
            }
          }
        }
        for(const w of [...writes]) {
          if(w.method==='delete'||!/^hotelStays\/[^/]+$/.test(w.args[0].path))continue;
          const old=reads.get(w.args[0].path)?.data()||{},next={...old,...w.args[1]};
          if(next.advanceBookingId && next.checkedOut===true && next.checkoutCompleted===true && !old.checkoutCompleted) {
            const ref=doc(database,'bookings',next.advanceBookingId),snap=await transaction.get(ref);
            if(snap.exists()&&snap.data().advanceAppliedStayId===w.args[0].id)
              writes.push({method:'update',args:[ref,{status:'checked_out'}]});
          }
        }
        for(const w of writes)transaction[w.method](...w.args);
        return result;
        } catch (error) {
          throw pmsCallbackError(error);
        }
      }, options);
      if(jpTouched){
        try{const {janakpuriCall}=await import('../services/janakpuriBookingService.js');await janakpuriCall('settle',{operationId});}
        catch(syncError){console.warn('PMS saved; booking status sync will retry on the next operation',syncError);}
      }
      return outcome;
    } catch (error) {
      if(jpPermitIssued){
        try{const {janakpuriCall}=await import('../services/janakpuriBookingService.js');await janakpuriCall('abort',{operationId});}
        catch(cleanupError){console.warn('Janakpuri temporary lock will expire safely',cleanupError);}
      }
      if(jpTouched && error.code==='functions/aborted' && attempt<4){
        await new Promise(resolve=>setTimeout(resolve,250*(2**attempt)));
        continue;
      }
      const revisionMayHaveChanged=error.code==='permission-denied' || (jpTouched && error.code==='functions/failed-precondition');
      if (!revisionMayHaveChanged || attempt >= 4 || boards.size === 0) throw error;
      let advanced = false;
      try {
        const checks = await Promise.all([...boards.values()].map(async ({reference, revision}) => {
          const fresh = await getDocFromServer(reference);
          return Number(fresh.data()?.revision || 0) > revision;
        }));
        advanced = checks.some(Boolean);
      } catch (_) { throw error; }
      if (!advanced) throw error;
    }
  }
}
